import { Form, Layout, Switch } from 'antd';
import { Button, Input, InformationBox } from '@prio365/prio365-react-library';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CreateProjectExtension,
  ProjectExtension,
  UpdateProjectExtension,
} from '../../../../../../models/ProjectExtension';
import { ProjectExtensionId, ProjectId } from '../../../../../../models/Types';
import { makePrioStyles } from '../../../../../../theme/utils';
import { colon } from '../../../../../../util/forms';
import Flex from '../../../../../../components/Flex';

const useStyles = makePrioStyles((theme) => ({
  rootLayout: {
    margin: 0,
    height: '100%',
    backgroundColor: theme.colors.application.background.default,
  },
  submitButton: {
    position: 'absolute',
    bottom: '32px',
  },
}));

export interface PrioShareProjectExtensionFormModel {
  displayName: string;
  hasDefaultAccess: boolean;
}

interface PrioShareProjectExtensionFormProps {
  projectExtension: ProjectExtension;
  addProjectExtension: CreateProjectExtension;
  projectId: ProjectId;
  updateProjectExtension: (
    projectId: ProjectId,
    projectExtensionId: ProjectExtensionId,
    projectExtension: UpdateProjectExtension
  ) => Promise<ProjectExtension>;
  createProjectExtension: (
    projectId: ProjectId,
    createProjectExtension: CreateProjectExtension
  ) => Promise<ProjectExtension>;
}

export const PrioShareProjectExtensionForm: React.FC<
  PrioShareProjectExtensionFormProps
> = (props) => {
  //#region ------------------------------ Defaults
  const {
    addProjectExtension,
    projectExtension,
    projectId,
    updateProjectExtension,
    createProjectExtension,
  } = props;
  const classes = useStyles();
  const [form] = Form.useForm();
  const { t } = useTranslation();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const [disabled, setDisabled] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const saveOrUpdateExtension = async (
    formModel: PrioShareProjectExtensionFormModel
  ) => {
    setLoading(true);
    setDisabled(true);

    if (projectExtension && !addProjectExtension) {
      let update: UpdateProjectExtension = {
        displayName: formModel.displayName,
        hasDefaultAccess: formModel.hasDefaultAccess,
        key: null,
        value: null,
      };
      await updateProjectExtension(
        projectId,
        projectExtension.projectExtensionId,
        update
      );
    } else if (addProjectExtension && !projectExtension) {
      let newProjectExtension: CreateProjectExtension = {
        displayName: formModel.displayName,
        hasDefaultAccess: formModel.hasDefaultAccess,
        projectExtensionType: 'prioShare',
        key: null,
        value: formModel.displayName,
      };
      await createProjectExtension(projectId, newProjectExtension);
    }

    setDisabled(false);
    setLoading(false);
  };
  //#endregion

  //#region ------------------------------ Effects
  useEffect(() => {
    if (projectExtension) {
      form.setFieldsValue({
        displayName: projectExtension.displayName,
        hasDefaultAccess: projectExtension.hasDefaultAccess,
      });
    }
  }, [projectExtension, form]);
  //#endregion

  return (
    <Layout className={classes.rootLayout}>
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          displayName: 'PrioShare',
          hasDefaultAccess: true,
        }}
        onFinish={(values) => {
          if (saveOrUpdateExtension) {
            saveOrUpdateExtension(values);
          }
        }}
      >
        <Form.Item
          name="displayName"
          colon={colon}
          label={t('projects:addIn.extensions.prioShare.form.displayName')}
          required={true}
          rules={[
            {
              required: true,
              message: t('projects:form.validation.displayNameRequired'),
            },
          ]}
        >
          <Input full disabled={loading || disabled} />
        </Form.Item>
        <InformationBox
          description={t(
            'projects:addIn.extensions.prioShare.displayNameDescription'
          )}
        />

        <Form.Item
          name="hasDefaultAccess"
          colon={colon}
          label={t('projects:addIn.extensions.form.hasDefaultAccess')}
          required={true}
          rules={[
            {
              required: true,
              message: t('projects:form.validation.hasDefaultAccessRequired'),
            },
          ]}
          valuePropName="checked"
        >
          <Switch disabled={loading || disabled} />
        </Form.Item>
        <InformationBox
          description={t(
            'projects:addIn.extensions.prioShare.hasDefaultAccessDescription'
          )}
        />

        <Flex.Row flex={1} justifyContent="flex-end">
          <Button
            htmlType="submit"
            loading={loading}
            disabled={loading || disabled}
            className={classes.submitButton}
          >
            {t('projects:addIn.extensions.form.save')}
          </Button>
        </Flex.Row>
      </Form>
    </Layout>
  );
};

export default PrioShareProjectExtensionForm;
